import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY10,
  FORTA_GRAY3,
  FORTA_GRAY8,
  FORTA_GRAY9,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { TP_OPTIONS_MENU_WIDTH } from './constants';

import type { SxProps, Theme } from '@mui/material/styles';

export const TP_HEADER_ROOT_SX: SxProps<Theme> = {
  px: '10px',
  py: '12px',
};

export const TP_HEADER_ROW_SX: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) 200px',
  columnGap: '40px',
  alignItems: 'start',
};

export const CLIENT_NAME_AUTOCOMPLETE_SX: SxProps<Theme> = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: 2,
    borderColor: FORTA_GRAY8,
    borderRadius: 2,
    // 2px border + 13px = notch gap starts 15px from the field edge
    padding: '0 13px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: FORTA_GRAY8,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 2,
    borderColor: FORTA_GRAY8,
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: FORTA_GRAY8,
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    fontFamily: 'DMSansMedium',
    fontSize: 13,
    // fieldset sits at top -5; a 12px legend centres the 2px top border on y 0..2
    height: 12,
  },
  '& .MuiOutlinedInput-notchedOutline legend > span': {
    paddingLeft: '6px',
    paddingRight: '6px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'DMSansMedium',
    fontSize: 13,
    lineHeight: '16px',
    color: FORTA_GRAY9,
    transform: 'translate(21px, -3px) scale(1)',
    maxWidth: 'calc(100% - 42px)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: FORTA_GRAY9,
  },
  // .MuiAutocomplete-inputRoot is doubled on purpose: it out-ranks MUI's clear-icon padding rule
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot.MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall':
    {
      minHeight: 53,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '21px',
      paddingRight: '34px',
      fontFamily: 'DMSansMedium',
      fontSize: 14,
      lineHeight: '16px',
      color: FORTA_GRAY9,
    },
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall .MuiAutocomplete-input':
    {
      padding: 0,
      height: 16,
    },
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot .MuiAutocomplete-endAdornment': {
    right: 10,
    top: 'calc(50% - 10px)',
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    width: 20,
    height: 20,
    padding: 0,
    margin: 0,
    color: FORTA_GRAY8,
  },
};

export const TP_OPTIONS_BUTTON_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'stretch',
  width: 200,
  height: 50,
  gap: '2px',
  padding: 0,
  border: 0,
  background: 'none',
};

export const TP_OPTIONS_LABEL_SEGMENT_SX: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px',
  backgroundColor: FORTA_BLUE.primary,
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  borderRadius: '8px 0 0 8px',
};

export const TP_OPTIONS_CHEVRON_SEGMENT_SX: SxProps<Theme> = {
  width: 34.17,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: FORTA_BLUE.primary,
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  borderRadius: '0 8px 8px 0',
};

export const TP_OPTIONS_MENU_PAPER_SX: SxProps<Theme> = {
  width: TP_OPTIONS_MENU_WIDTH,
  mt: '4px',
  bgcolor: FORTA_BLUE.light3,
  border: `1px solid ${FORTA_BLUE.light1}`,
  borderRadius: '16px',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  // The submenu opens outside the paper, so the paper itself must not clip.
  overflow: 'visible',
};

export const TP_OPTIONS_MENU_LIST_SX: SxProps<Theme> = {
  p: 0,
  borderRadius: '16px',
  overflow: 'hidden',
};

export const TP_OPTIONS_ROW_SX: SxProps<Theme> = {
  height: 48,
  minHeight: 48,
  p: 0,
  gap: '10px',
  fontFamily: 'DMSansMedium',
  fontSize: 16,
  lineHeight: '31px',
  color: FORTA_GRAY9,
  '&:hover, &.Mui-focusVisible': {
    bgcolor: FORTA_BLUE.primary,
    color: FORTA_WHITE,
  },
  '&.Mui-disabled': {
    opacity: 1,
    color: FORTA_GRAY3,
  },
};

export const TP_OPTIONS_ROW_CHEVRON_SX: SxProps<Theme> = {
  width: 27.08,
  height: 34.17,
  p: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    transform: 'rotate(90deg)',
  },
};

export const TP_OPTIONS_ROW_CHEVRON_HIDDEN_SX: SxProps<Theme> = {
  width: 27.08,
  height: 34.17,
  p: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  opacity: 0,
};

export const TP_OPTIONS_DIVIDER_SX: SxProps<Theme> = {
  height: 20,
  display: 'flex',
  alignItems: 'center',
  px: '52px',
};

export const TP_OPTIONS_DIVIDER_LINE_SX: SxProps<Theme> = {
  flex: 1,
  borderTop: `1px solid ${FORTA_GRAY10}`,
};

export const TP_OPTIONS_SUBMENU_PAPER_SX: SxProps<Theme> = {
  position: 'absolute',
  right: '100%',
  width: TP_OPTIONS_MENU_WIDTH,
  bgcolor: FORTA_BLUE.light3,
  border: `1px solid ${FORTA_BLUE.light1}`,
  borderRadius: '16px',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  overflow: 'hidden',
};

export const TP_OPTIONS_SUBMENU_ROW_SX: SxProps<Theme> = {
  height: 48,
  minHeight: 48,
  px: '10px',
  py: 0,
  fontFamily: 'DMSansMedium',
  fontSize: 16,
  lineHeight: '31px',
  color: FORTA_GRAY9,
  '&:hover, &.Mui-focusVisible': {
    bgcolor: FORTA_BLUE.primary,
    color: FORTA_WHITE,
  },
  '&.Mui-disabled': {
    opacity: 1,
    color: FORTA_GRAY3,
  },
};

export const SELECT_PLAN_ROW_ICON_SX: SxProps<Theme> = {
  width: 24,
  display: 'flex',
  alignItems: 'center',
};

export const SELECT_PLAN_ROW_STATUS_SX: SxProps<Theme> = {
  ml: 1,
  display: 'flex',
  alignItems: 'center',
};

export const IN_PROGRESS_NOTICE_SX: SxProps<Theme> = {
  px: '10px',
  py: 1,
  fontFamily: 'DMSansMedium',
  fontSize: 16,
  color: FORTA_GRAY9,
};

export const IN_PROGRESS_LINK_SX: SxProps<Theme> = {
  font: 'inherit',
  color: FORTA_BLUE.primary,
  textDecoration: 'underline',
  cursor: 'pointer',
};

export const CLONE_DIALOG_TEXT_SX: SxProps<Theme> = {
  fontSize: 18,
};

export const CLONE_DIALOG_ACTIVE_SX: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: 18,
  color: FORTA_SUCCESS,
};

export const CLONE_DIALOG_INACTIVE_SX: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: 18,
  color: FORTA_ERROR,
};

export const DELETE_DIALOG_TITLE_SX: SxProps<Theme> = {
  fontSize: 20,
  fontWeight: 700,
};

export const DELETE_DIALOG_WARNING_SX: SxProps<Theme> = {
  fontSize: 21,
  fontWeight: 700,
};

export const HIDDEN_PDF_SX: SxProps<Theme> = {
  visibility: 'hidden',
  position: 'absolute',
  top: '-9999px',
  width: '100vw',
};
